import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { BarChart, CartesianGrid, Label, XAxis, YAxis, Bar, ResponsiveContainer, Tooltip } from 'recharts';

import { Text, TextType } from 'ui-components/components/text';

import { useIsShipStationAnalyticsFeatureEnabled } from '../../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import { getInterval } from '../../helper';
import { emptyArray } from 'ui-components/default-props/empty';
import Colors from '../utilities/colors';
import { MAIN_GRAPH_HEIGHT, Y_AXIS_TICK_LENGTH } from '../utilities/constants';

import CustomTooltip from '../custom-tooltip';
import CustomTooltipShipmentsByAge from '../custom-tooltip/custom-tooltip-shipments-by-age';
import { useUserDateFormat } from '../../../../../hooks/use-datetime-formats';

import messages from './messages';
import style from './style.module.less';
import analyticsStyle from '../../../analytics-style.module.less';

const ShipmentsByAgeGraph = ({ data = emptyArray }) => {
  const intl = useIntl();
  const { formatUserDate } = useUserDateFormat();
  const formattedData = useMemo(
    () =>
      data.map((datum) => ({
        ...datum,
        count: datum.count,
      })),
    [data],
  );

  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();

  const formatXTicks = useCallback(
    (date) => {
      return formatUserDate(date, false);
    },
    [formatUserDate],
  );

  const interval = useMemo(() => getInterval(formattedData), [formattedData]);

  return (
    <>
      {isShipStationAnalyticsFeatureEnabled && (
        <Text className={analyticsStyle['analytics-section-title']} textType={TextType.H4} value={messages.shipmentOverviewReportTitle} />
      )}
      <ResponsiveContainer height={MAIN_GRAPH_HEIGHT} width='100%'>
        <BarChart
          data={formattedData}
          style={{
            fontSize: 12,
          }}
        >
          <CartesianGrid stroke={Colors.GREY_HEX_VALUE} />
          <XAxis dataKey='interval' interval={interval} tickFormatter={formatXTicks} tickLine={false} />
          <YAxis allowDecimals={false} tickLine={false} tickSize={Y_AXIS_TICK_LENGTH}>
            <Label
              angle={-90}
              className={classnames(style['yAxis-label'], {
                [analyticsStyle['analytics-yAxis-label']]: isShipStationAnalyticsFeatureEnabled,
              })}
              position='insideLeft'
              value={intl.formatMessage(messages.shipments)}
            />
          </YAxis>
          <Tooltip
            content={
              <CustomTooltip>
                <CustomTooltipShipmentsByAge />
              </CustomTooltip>
            }
          />
          <Bar dataKey='count' fill={Colors.BAR_COLOR_PURPLE} />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

ShipmentsByAgeGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      interval: PropTypes.string,
      count: PropTypes.number,
    }),
  ),
};

export default ShipmentsByAgeGraph;
